import { ElementRef, Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
    IIqTrendlinesGraphLtStateService,
    RatioMap,
    TL_GRAPH_LT_URL,
    TRENDLINES_CACHE_DB,
    TRENDLINES_FLATBED_LT_GRAPH_DOC,
    TRENDLINES_REEFER_LT_GRAPH_DOC,
    TRENDLINES_VAN_LT_GRAPH_DOC,
    LoadTruckResponse,
    ThreeYearMonthly,
    Ratio,
} from '../../models';
import * as FlatbedGraph from './graph-flatbed';
import * as ReeferGraph from './graph-reefer';
import * as VanGraph from './graph-van';
import { lastValueFrom } from 'rxjs';
import { isNil } from 'lodash';
import { DateTime } from 'luxon';
import { DatDbClientService } from '@dat/db';
import { DatiqAppConfig } from '@dat/datiq-core/models';

// TODO: Implement after we decide on the visualization and get information on L/T for the past

type LtResponse = {
    code: string;
    loads: number;
    trucks: number;
    ratio: number;
};

type CachedLtData = {
    data: RatioMap;
    expiryDate: string;
};

const LT_DOC = new Map<string, string>([
    ['Van', TRENDLINES_VAN_LT_GRAPH_DOC],
    ['Reefer', TRENDLINES_REEFER_LT_GRAPH_DOC],
    ['Flatbed', TRENDLINES_FLATBED_LT_GRAPH_DOC],
]);
@Injectable({
    providedIn: 'root',
})
export class IqTrendlinesGraphLtStateService
    implements IIqTrendlinesGraphLtStateService
{
    private _url: string = this._getAnalyticsUrl() + inject(TL_GRAPH_LT_URL);
    private _db = inject(DatDbClientService);
    private PACIFIC_TIME_ZONE: string = 'America/Los_Angeles';
    private DB_OPTS: { dbName: string } = {
        dbName: TRENDLINES_CACHE_DB,
    };

    constructor(
        private _httpClient: HttpClient,
        private _appConfig: DatiqAppConfig,
    ) {}

    private _getAnalyticsUrl(): string {
        return this._appConfig?.coreConfig?.ANALYTICS_BASE_URL ?? '';
    }

    private _nextDay(): Date {
        //Next day at 7am
        return DateTime.utc()
            .setZone(this.PACIFIC_TIME_ZONE)
            .plus({ days: 1 })
            .set({
                hour: 7,
                minute: 0,
                second: 0,
                millisecond: 0,
            })
            .toJSDate();
    }

    private _isDataStale(expiryDate: string): boolean {
        const pacificExpiryDate: DateTime = DateTime.fromISO(
            expiryDate,
        ).setZone(this.PACIFIC_TIME_ZONE);
        if (isNil(pacificExpiryDate)) return true;
        const currentPacificTime: DateTime = DateTime.utc().setZone(
            this.PACIFIC_TIME_ZONE,
        );
        return currentPacificTime > pacificExpiryDate;
    }

    async vanData(): Promise<RatioMap> {
        return this._fetchLtData('Van');
    }

    async reeferData(): Promise<RatioMap> {
        return this._fetchLtData('Reefer');
    }

    async flatbedData(): Promise<RatioMap> {
        return this._fetchLtData('Flatbed');
    }

    async flatbedGraph(container: ElementRef<any>, ratioMap: RatioMap) {
        FlatbedGraph.render(container, ratioMap);
    }
    async reeferGraph(container: ElementRef<any>, ratioMap: RatioMap) {
        ReeferGraph.render(container, ratioMap);
    }
    async vanGraph(container: ElementRef<any>, ratioMap: RatioMap) {
        VanGraph.render(container, ratioMap);
    }

    private async _fetchLtData(equipment: string): Promise<RatioMap> {
        const docName = LT_DOC.get(equipment);
        const hasDoc = await lastValueFrom(
            this._db.hasDoc(docName, this.DB_OPTS),
        );
        if (!hasDoc) {
            const data: RatioMap = await this._fetchAndCacheLtData(
                equipment,
                docName,
            );
            return data;
        }
        const cachedData: CachedLtData = (await lastValueFrom(
            this._db.getDoc(docName, this.DB_OPTS),
        )) as CachedLtData;
        if (this._isDataStale(cachedData.expiryDate)) {
            const data: RatioMap = await this._fetchAndCacheLtData(
                equipment,
                docName,
            );
            return data;
        }
        return this._parseCachedData(cachedData.data); // We need to do this because PouchDB has this wierd habit of automatically converting dates to ISO Strings
    }

    private async _fetchAndCacheLtData(
        equipment: string,
        docName: string,
    ): Promise<RatioMap> {
        const url = this._url.replace('%s', equipment.toUpperCase());
        const response: LoadTruckResponse = await lastValueFrom(
            this._httpClient.get<LoadTruckResponse>(url),
        );
        if (!isNil(response?.threeYearMonthly)) {
            const filteredData: RatioMap = this._filterDataIntoyears(
                response.threeYearMonthly,
            );
            const sortedDate: RatioMap = this._sortData(filteredData);
            this._cacheLtData(sortedDate, docName);
            return Promise.resolve(sortedDate);
        }
        return Promise.resolve({
            twoYearPast: [],
            oneYearPast: [],
            currentYear: [],
        });
    }

    private _cacheLtData(data: RatioMap, docName: string): void {
        const expiryDate: Date = this._nextDay();
        const cache: CachedLtData = {
            data,
            expiryDate: expiryDate.toISOString(),
        };
        this._db.setDoc(docName, cache, this.DB_OPTS);
    }

    private _filterDataIntoyears(data: ThreeYearMonthly[]): RatioMap {
        const currentDate: DateTime = DateTime.now().minus({ month: 1 });

        const twoYearsPast: number = currentDate.minus({ years: 2 }).year;
        const oneYearPast: number = currentDate.minus({ years: 1 }).year;
        const currentYear: number = currentDate.year;

        const filteredData: RatioMap = {
            twoYearPast: [],
            oneYearPast: [],
            currentYear: [],
        };
        data.forEach((datum: ThreeYearMonthly) => {
            const year = DateTime.fromFormat(
                datum?.monthAndYearWhen,
                'yyyy-MM',
            ).year;
            const ratio: Ratio = {
                date: DateTime.fromFormat(
                    datum?.monthAndYearWhen,
                    'yyyy-MM',
                ).toJSDate(),
                lt: datum.ratio,
            };
            if (year === twoYearsPast) {
                filteredData.twoYearPast.push(ratio);
            } else if (year === oneYearPast) {
                filteredData.oneYearPast.push(ratio);
            } else if(year === currentYear) {
                filteredData.currentYear.push(ratio);
            }
        });

        return filteredData;
    }

    private _sortData(data: RatioMap): RatioMap {
        const sortedData: RatioMap = {
            twoYearPast: [],
            oneYearPast: [],
            currentYear: [],
        };

        sortedData.twoYearPast = data.twoYearPast.sort(
            (a: Ratio, b: Ratio) => a.date.getTime() - b.date.getTime(),
        );
        sortedData.oneYearPast = data.oneYearPast.sort(
            (a: Ratio, b: Ratio) => a.date.getTime() - b.date.getTime(),
        );
        sortedData.currentYear = data.currentYear.sort(
            (a: Ratio, b: Ratio) => a.date.getTime() - b.date.getTime(),
        );

        return sortedData;
    }

    private _parseCachedData(data: RatioMap): RatioMap {
        const parsedData: RatioMap = {
            twoYearPast: [],
            oneYearPast: [],
            currentYear: [],
        };
        parsedData.twoYearPast = data.twoYearPast.map((datum: Ratio) => {
            return {
                date: new Date(datum.date),
                lt: datum.lt,
            };
        });
        parsedData.oneYearPast = data.oneYearPast.map((datum: Ratio) => {
            return {
                date: new Date(datum.date),
                lt: datum.lt,
            };
        });
        parsedData.currentYear = data.currentYear.map((datum: Ratio) => {
            return {
                date: new Date(datum.date),
                lt: datum.lt,
            };
        });
        return parsedData;
    }
}
